import {
  ACCEPT,
  GRAMMAR,
  PLAGIARISM,
  READ,
  REJECT,
} from '@src/components/Cards/constants'
import { GRAMMAR_ANALYZE, PLAGIARISM_ANALYZE } from '@src/queries/Analyze'
import { GrammarAnalyze } from '@src/queries/GrammarAnalyze'
import { PlagiarismAnalyze } from '@src/queries/PlagiarismAnalyze'
import {
  acceptCard,
  CardActionsPayload,
  CheckMeta,
  checkResults,
  CheckResultsError,
  checksCancelled,
  checksComplete,
  checkStart,
  checkStartFail,
  clearChecks,
  partialPlagiarismCheckEnd,
  partialPlagiarismCheckStart,
  pusherRunPlagiarismCheck,
  readCard,
  rejectCard,
  runChecks,
  RunChecksPayload,
  setCheck,
  setExpectedChecks,
  updateCardState,
} from '@src/state/checks/actions'
import { RootState } from '@src/state/types'
import { CheckType, CheckWithMetadata, Status } from '@src/types/checks'
import { flagCardsForDemoText } from '@src/utils/cards'
import { captureError } from '@src/utils/errors'
import graphApi from '@src/utils/http-clients/graphApi'
import wtaiApi from '@src/utils/http-clients/wtaiApi'
import { isExpertCheckPage, rootMatch } from '@src/utils/routes'
import { LOCATION_CHANGE, RouterState } from 'connected-react-router'
import { ContentState, EditorState } from 'draft-js'
import * as cookie from 'js-cookie'
import { createLogic } from 'redux-logic'
import { getType } from 'typesafe-actions'
import { getPaidMember } from '../auth/selectors'
import {
  clearDocument,
  deleteAllGrammarComments,
  deleteAllPlagiarismComments,
  gateComments,
  saveDocument,
  setEditorState,
  setPlagiarismScore,
  updateComments,
} from '../document/actions'
import { deleteCommentFromEditor } from '../document/helpers/draftjs.helper'
import {
  getCategoryFromType,
  getEditorText,
  selectAllComments,
  selectForMinTextLength,
  selectFreemiumPlagText,
  selectInteractableGrammarComments,
  selectInteractablePlagComments,
} from '../document/selectors'
import { getCurrentVariant } from '../experiment/selectors'
import { CONFIRM_LEAVE_MODAL, hideModal } from '../modals/actions'
import {
  handleCheckerErrors,
  openPusherConnection,
  pusherAnalyze,
  PusherAnalyzePayload,
  pusherHandleChecks,
  pusherPartialPlagiarismAnalyze,
  pusherPlagiarismAnalyze,
} from '../pusher/actions'
import { CheckerError } from '../pusher/types'
import { isDemoSelector } from '../router/selectors'
import { getUploadId } from '../workspace/selectors'
import {
  FF_COOKIE_NAME,
  GRAMMAR_CHECK_START_FAIL_MESSAGE,
  GRAMMAR_CHECK_TIMEOUT,
  PARTIAL_PLAGIARISM_CHECK_START_FAIL_MESSAGE,
  PLAGIARISM_CHECK_START_FAIL_MESSAGE,
  PLAGIARISM_CHECK_TIMEOUT,
  PLAGIARISM_CHECK_TIMEOUT_MESSAGE,
  WTAI_CHECK_DESTINATIONS,
} from './constants'
import {
  allChecksComplete,
  getIncompleteGrammarChecks,
  selectAnalyzeOptions,
  shouldCardUpdate,
} from './selectors'
import { setNewRelicPageAction } from '@src/utils/newrelic'
import { Cookies, getCookie } from '@src/utils/cookies'
import { EnableCLSandboxValues } from '../featureFlags/types'
import { selectDisablePartialPlagiarismCheck } from '../featureFlags/selectors'

export const locationChangeLogic = createLogic<RootState, RouterState>({
  type: LOCATION_CHANGE,
  process({ action }, dispatch, done) {
    const { pathname } = action.payload.location
    if (pathname === rootMatch) {
      dispatch(clearChecks())
      dispatch(clearDocument())
    }
    done()
  },
})

export const pusherPlagiarismAnalyzeLogic = createLogic<RootState, PusherAnalyzePayload>({
  type: getType(pusherPlagiarismAnalyze),
  async process({ getState, action }, dispatch, done) {
    const state = getState()
    const uploadId = getUploadId(state)
    const currentVariant = getCurrentVariant(state)
    const { traceId, isRecheck } = action.payload
    const wtEnableCLSandbox =
      getCookie(Cookies.EnableCLSandbox) === EnableCLSandboxValues.true
        ? EnableCLSandboxValues.true
        : EnableCLSandboxValues.false

    setNewRelicPageAction('plagiarismCheckInitiated', { traceId })
    localStorage.setItem(
      'plagCheckStart',
      JSON.stringify({
        traceId,
        timestamp: Date.now(),
        isPartialPlag: false,
      })
    )
    dispatch(checkStart({ type: 'plagiarism', isRecheck }, {}))

    try {
      const text = getEditorText(state)
      await graphApi.mutate<PlagiarismAnalyze>({
        mutation: PLAGIARISM_ANALYZE,
        variables: {
          recipients: [
            {
              destination: WTAI_CHECK_DESTINATIONS.PUSHER,
              token: traceId,
            },
          ],
          text,
          wtTraceId: traceId,
          wtUploadId: uploadId,
          wtVariant: currentVariant,
        },
        context: {
          headers: {
            wtEnableCLSandbox,
          },
        },
      })

      dispatch(setExpectedChecks({ checks: ['plagiarism'] }, {}))
    } catch (error) {
      const checkError = new Error(PLAGIARISM_CHECK_START_FAIL_MESSAGE)
      checkError.stack = error
      checkError.name = 'WTAI PLAGIARISM FAILED'

      captureError(error)

      dispatch(
        checkStartFail({
          plagiarism: true,
          error: checkError,
        })
      )
    }
    done()
  },
})

export const pusherPartialPlagiarismAnalyzeLogic = createLogic<
  RootState,
  PusherAnalyzePayload
>({
  type: getType(pusherPartialPlagiarismAnalyze),
  validate({ action, getState }, allow, reject) {
    const state = getState()
    if (selectForMinTextLength(state) && !getPaidMember(state)) {
      return allow(action)
    }
    reject(action)
  },
  async process({ getState, action }, dispatch, done) {
    const state = getState()
    const { traceId } = action.payload
    const { id } = state.workspace
    dispatch(partialPlagiarismCheckStart())
    try {
      localStorage.setItem(
        'plagCheckStart',
        JSON.stringify({
          traceId,
          timestamp: Date.now(),
          isPartialPlag: true,
        })
      )
      const state = getState()

      const text = selectFreemiumPlagText(state)
      const currentVariant = getCurrentVariant(state)

      const wtEnableCLSandbox =
        getCookie(Cookies.EnableCLSandbox) === EnableCLSandboxValues.true
          ? EnableCLSandboxValues.true
          : EnableCLSandboxValues.false

      await wtaiApi.post(
        '/plagiarism/api/v1.1/analyze-free',
        {
          recipients: [
            {
              destination: WTAI_CHECK_DESTINATIONS.PUSHER,
              token: traceId,
            },
          ],
          text,
        },
        {
          headers: {
            'X-WT-TRACE-ID': traceId,
            'WT-VARIANT': currentVariant,
            'WT-UPLOAD-ID': id,
            'WT-ENABLE-CL-SANDBOX': wtEnableCLSandbox,
          },
        }
      )
    } catch (error) {
      const { checksRunning } = state.workspace
      const { checks } = state
      const checkError = new Error(PARTIAL_PLAGIARISM_CHECK_START_FAIL_MESSAGE)
      checkError.stack = error
      checkError.name = 'WTAI PLAGIARISM FAILED'

      captureError(error)

      dispatch(
        checkStartFail({
          plagiarism: true,
          error: checkError,
        })
      )
      dispatch(partialPlagiarismCheckEnd({ plagiarismFound: false }))
      // We can land up in a sitation where the freemium plagiarism endpoint
      // throttles the plagiarism requests. In that case, we should still gate
      // the comments if the checks are running, else we might land up in a situation
      // where plagiarism check finishes at the end (which is highly likely)
      // but we do not gate comments.
      if (checksRunning || Object.keys(checks).length > 0) {
        dispatch(gateComments())
      }
    }
    done()
  },
})

export const updateCardStateLogic = createLogic<RootState, CardActionsPayload>({
  type: getType(updateCardState),
  validate({ action }, allow, reject) {
    const { card, newState } = action.payload as CardActionsPayload

    const { cardState } = card
    if (shouldCardUpdate(cardState, newState)) {
      allow(action)
    } else {
      reject(action)
    }
  },
  async process({ action }, dispatch, done) {
    const { newState } = action.payload as CardActionsPayload

    switch (newState) {
      case ACCEPT: {
        dispatch(acceptCard(action.payload as CardActionsPayload))
        break
      }
      case REJECT: {
        dispatch(rejectCard(action.payload as CardActionsPayload))
        break
      }
      case READ: {
        dispatch(readCard(action.payload as CardActionsPayload))
        break
      }
    }
    done()
  },
})

export const pusherRunChecksLogic = createLogic<RootState, RunChecksPayload, CheckMeta>({
  type: getType(runChecks),
  validate({ getState, action }, allow, reject) {
    const { checksRunning } = getState().workspace
    if (checksRunning) {
      reject(action)
    } else {
      allow(action)
    }
  },
  async process({ action }, dispatch, done) {
    const { checkCategory } = action.payload

    switch (checkCategory) {
      case PLAGIARISM: {
        dispatch(
          pusherRunPlagiarismCheck({ ...action.payload }, { origin: action.meta.origin })
        )
        break
      }
      case GRAMMAR:
        dispatch(
          openPusherConnection({
            isRecheck: action.payload.isRecheck,
            origin: action.meta.origin,
          })
        )
        break
      default:
    }

    done()
  },
})

export const clearCommentsFromEditorLogic = createLogic<RootState, RunChecksPayload>({
  type: [getType(runChecks)],
  validate({ getState, action }, allow, reject) {
    const { router } = getState()
    if (isExpertCheckPage(router?.location.pathname)) {
      reject(action)
    }
    const state = getState()
    const hasComments = selectAllComments(state).length > 0
    const { checksRunning } = state.workspace

    if (!hasComments || checksRunning) {
      reject(action)
    }

    allow(action)
  },
  process({ action, getState }, dispatch, done) {
    const state = getState()
    const { checkCategory } = action.payload
    const { editorState } = state.document
    const comments = selectAllComments(state)
    checkCategory === PLAGIARISM
      ? dispatch(deleteAllPlagiarismComments())
      : dispatch(deleteAllGrammarComments())

    const newContentState = comments
      .filter(c => getCategoryFromType(c.type) === checkCategory)
      .reduce<ContentState>((cs, comment) => {
        const { id, entityKeys } = comment
        return deleteCommentFromEditor({ id, entityKeys, contentState: cs })
      }, editorState.getCurrentContent())

    const newEditorState = EditorState.set(editorState, {
      currentContent: newContentState,
    })
    dispatch(setEditorState({ editorState: newEditorState }))
    done()
  },
})

export const pusherRunPlagiarismCheckLogic = createLogic<
  RootState,
  RunChecksPayload,
  CheckMeta
>({
  type: getType(pusherRunPlagiarismCheck),
  process({ action }, dispatch, done) {
    const { origin } = action.meta
    const { isRecheck } = action.payload
    dispatch(openPusherConnection({ isPlagiarism: true, origin, isRecheck }))
    done()
  },
})

export const pusherHandleChecksLogic = createLogic<RootState, CheckWithMetadata>({
  type: getType(pusherHandleChecks),
  validate({ getState, action }, allow, reject) {
    const { type } = action.payload
    const stateCheck = getState().checks[type]
    if (stateCheck && stateCheck.status === 'FAILED') {
      return reject(action)
    }
    allow(action)
  },
  process({ action, getState }, dispatch, done) {
    const state = getState()
    const isDemo = isDemoSelector(state)

    const actionCheck = action.payload
    const { summary, ...rest } = actionCheck

    const isPartialPlagiarismCheck =
      actionCheck.type === 'plagiarism' && state.workspace.partialPlagiarismRunning

    if (isPartialPlagiarismCheck) {
      const plagiarismFound = !!actionCheck.metadata.length
      dispatch(partialPlagiarismCheckEnd({ plagiarismFound }))
    }

    if (!isPartialPlagiarismCheck || isDemo) {
      dispatch(
        setCheck({
          ...rest,
        })
      )
      if (typeof summary?.overallMatchPercentage === 'number') {
        const score = summary.overallMatchPercentage
        dispatch(setPlagiarismScore({ score }))
        dispatch(
          saveDocument.request({
            document: { ...state.document, plagiarismScore: score },
          })
        )
      }
      dispatch(checkResults.success({ ...actionCheck, finished: true }))
    } else {
      dispatch(gateComments()) // sets checksComplete
    }

    done()
  },
})

export const pusherAnalyzeLogic = createLogic<RootState, PusherAnalyzePayload>({
  type: getType(pusherAnalyze),
  validate({ action }, allow, reject) {
    if (action.payload.isPlagiarism) {
      return reject(pusherPlagiarismAnalyze(action.payload))
    }
    allow(action)
  },
  async process({ getState, action }, dispatch, done) {
    const { traceId, isRecheck } = action.payload

    const state = getState()
    const text = getEditorText(state)
    const currentVariant = getCurrentVariant(state)

    const { id } = state.workspace

    if (!isRecheck) {
      const disableParialPlagCheck = selectDisablePartialPlagiarismCheck(state)
      !disableParialPlagCheck && dispatch(pusherPartialPlagiarismAnalyze({ traceId }))
    }
    try {
      const featureFlags = cookie.get(FF_COOKIE_NAME)

      const getCheckerTypesFromQuery = async () => {
        const { data } = await graphApi.mutate<GrammarAnalyze>({
          mutation: GRAMMAR_ANALYZE,
          variables: {
            recipients: [
              {
                destination: WTAI_CHECK_DESTINATIONS.PUSHER,
                token: traceId,
              },
            ],
            options: selectAnalyzeOptions(),
            text,
            wtTraceId: traceId,
            wtUploadId: id,
            wtVariant: currentVariant,
            wtFF: featureFlags,
          },
        })
        const checkerTypes = data?.grammarAnalyze?.message?.checker_types
        const filteredCheckerTypes = checkerTypes?.filter(
          (checker): checker is string => !!checker
        )
        return (filteredCheckerTypes || []) as CheckType[]
      }
      const checkerTypes: CheckType[] = await getCheckerTypesFromQuery()

      checkerTypes.forEach(checker => {
        dispatch(checkStart({ type: checker, isRecheck }, {}))
      })
      dispatch(setExpectedChecks({ checks: checkerTypes }, {}))
    } catch (error) {
      const checkError = new Error(GRAMMAR_CHECK_START_FAIL_MESSAGE)

      checkError.stack = error?.stack

      dispatch(
        checkStartFail({
          grammar: true,
          error: checkError,
        })
      )

      captureError(checkError)
    }
    done()
  },
})

export const plagiarismCheckTimeoutLogic = createLogic<RootState>({
  type: [getType(pusherPlagiarismAnalyze), getType(pusherPartialPlagiarismAnalyze)],
  warnTimeout: 0,
  cancelType: [getType(checkResults.success), getType(partialPlagiarismCheckEnd)],
  process({ action, cancelled$ }, dispatch, done) {
    const timeout = setTimeout(() => {
      triggerNRPageAction()
      const type =
        action.type === getType(pusherPlagiarismAnalyze)
          ? 'plagiarism'
          : 'freemiumPlagiarism'
      const error = new Error(PLAGIARISM_CHECK_TIMEOUT_MESSAGE)
      dispatch(checkResults.failure({ type, status: 'FAILED', ...error }))
      captureError(error)
      clearTimeout(timeout)
      done()
    }, PLAGIARISM_CHECK_TIMEOUT)
    cancelled$.subscribe(() => {
      clearTimeout(timeout)
    })
  },
})

export const grammarCheckTimeoutLogic = createLogic<RootState>({
  type: getType(pusherAnalyze),
  warnTimeout: 0,
  cancelType: getType(checksComplete),
  process({ getState, cancelled$ }, dispatch, done) {
    const timeout = setTimeout(() => {
      const incompleteChecks = getIncompleteGrammarChecks(getState())
      incompleteChecks.forEach((type: CheckType) => {
        const error = new Error(`GRAMMAR_CHECK_TIMEOUT for ${type}`)
        dispatch(checkResults.failure({ type, ...error, status: 'FAILED' }))
        captureError(error)
      })

      clearTimeout(timeout)
      done()
    }, GRAMMAR_CHECK_TIMEOUT)

    cancelled$.subscribe(() => {
      clearTimeout(timeout)
    })
  },
})

export const checkFailureLogic = createLogic<RootState, CheckResultsError>({
  type: getType(checkResults.failure),
  process({ getState, action }, dispatch, done) {
    const state = getState()
    const { checksToExpect } = state.workspace
    const mergedCheckState = {
      ...state.checks,
      [action.payload.type]: {
        finished: true,
        count: 0,
        status: 'FAILED' as Status,
        type: action.payload.type,
      },
    }
    if (allChecksComplete(mergedCheckState, checksToExpect)) {
      dispatch(checksComplete())
    }
    done()
  },
})

export const handleCheckerErrorsLogic = createLogic<RootState, CheckerError>({
  type: getType(handleCheckerErrors),
  process({ action }, dispatch, done) {
    const { checkerType, message } = action.payload
    switch (message) {
      default:
        dispatch(
          checkResults.failure({
            type: checkerType,
            message,
            name: 'Checker Error',
            status: 'failed',
          })
        )
    }
    done()
  },
})

export const handleChecksCompleteLogic = createLogic<RootState>({
  type: getType(checksComplete),
  cancelType: [getType(checksCancelled), getType(checkResults.failure)],
  process({ getState }, dispatch, done) {
    const state = getState()
    triggerNRPageAction()
    if (isDemoSelector(state) && !getPaidMember(state)) {
      // Flag grammar cards for demo text
      const allGrammarCards = selectInteractableGrammarComments(state)
      const firstFiveFlaggedGrammarCards = flagCardsForDemoText(allGrammarCards, 5)
      dispatch(updateComments(firstFiveFlaggedGrammarCards))

      // Flag grammar and plagiarism cards for CTA banners
      const firstPlagComment = selectInteractablePlagComments(state)[0]
      if (firstPlagComment) {
        firstPlagComment.showCtaBanner = true
      }
      const sixthGrammarComment = selectInteractableGrammarComments(state)[5]
      if (sixthGrammarComment) {
        sixthGrammarComment.showCtaBanner = true
      }
      dispatch(updateComments([firstPlagComment, sixthGrammarComment]))
    }

    dispatch(hideModal({ name: CONFIRM_LEAVE_MODAL }, {}))
    done()
  },
})

export const checkLogic = [
  locationChangeLogic,
  clearCommentsFromEditorLogic,
  updateCardStateLogic,
  pusherRunChecksLogic,
  pusherHandleChecksLogic,
  pusherAnalyzeLogic,
  pusherPartialPlagiarismAnalyzeLogic,
  pusherPlagiarismAnalyzeLogic,
  pusherRunPlagiarismCheckLogic,
  plagiarismCheckTimeoutLogic,
  grammarCheckTimeoutLogic,
  checkFailureLogic,
  handleCheckerErrorsLogic,
  handleChecksCompleteLogic,
]

const triggerNRPageAction = () => {
  const plagCheckStartObj = JSON.parse(localStorage.getItem('plagCheckStart') || '{}')
  const plagDuration = Date.now() - plagCheckStartObj.timestamp
  plagCheckStartObj.traceId &&
    plagDuration / 1000 < 601 &&
    setNewRelicPageAction('plagCheckCompleted', {
      scanId: plagCheckStartObj.traceId,
      plagDuration,
      isPartialPlag: plagCheckStartObj.isPartialPlag,
    })
  localStorage.removeItem('plagCheckStart')
}



// WEBPACK FOOTER //
// ./src/state/checks/logic.ts